import { isArabicUi } from "@/shared/config/arabicUi";
import type { MeetingKind } from "@/shared/config/pointsRules";
import type { SphereKey } from "./spheres";

/**
 * Подписи чипов и подсказок. Живут в фиче, а не в общих словарях
 * (shared/config/locales/*.json): это предпросмотр для администраторов, и
 * общие словари той же ночью правят другие ветки — лишний конфликт на ровном
 * месте. Когда правила утвердят и чипы откроют всем, строки переедут туда.
 *
 * Правило текстов (владелец 29.09): коротко, по-игровому, «за что → сколько»; ни денег
 * (ни цен, ни «за каждый $1»), ни порогов и пределов. У приглашения первой — выгода самого
 * человека (10 % с покупок друга на баланс). Баллы за покупку друга — числом и словами,
 * за что (владелец 03.10: «в этой строчке не написано сколько баллов там и не ясно что
 * значит invited points for their purchases»): «+18 500, когда друг купит эту часть».
 */
export type PointsTexts = {
    sphere: Record<SphereKey, string>;
    /** «+50 000 вклад за подарок» и т. п. — после суммы. */
    perPurchase: string;
    /** «+500 вклад · за отзыв» (таблица v4). */
    forReview: string;
    /** «−20 % владельцу предыдущей части» (доска PTSD_1). */
    nextPartDiscount: (percent: number) => string;
    /** «+50 вклад · после проверки» (доски PTSD_0, PTSD_4; как проверяют — не пишем). */
    afterAi: string;
    /** «+50 обучение · за выбранный вопрос» (таблица v4). */
    forQuestion: string;
    /** «+150 обучение · один раз за этот тест» (доска PTSD_2). */
    oncePerTest: string;
    /** «+2 000 тренировки · за неделю строго по плану» (доска PTSD_5). */
    weekOnPlan: string;
    /** Карточка «Подарить другу» на странице партнёрки (доска V4_R2). */
    giftTitle: string;
    /** «Ссылка есть у каждого курса ниже: другу −10 %, а вам — 10 % с его покупки на баланс». */
    giftBody: (discountPct: number, balancePct: number) => string;
    /** Подарок (таблица v8.2): дарителю — вклад по цене подарка: «+15 000 вклад за подарок». */
    contribForGift: string;
    /** Подсказки «за что → сколько» (владелец 29.09): общение, встречи, приглашение, обучение. */
    hint: {
        /** Вид встречи по таблице: «Встреча здоровья · +1 600». */
        meeting: Record<MeetingKind, string>;
        /** «Встреча здоровья · +1 600 каждому, кто придёт» — при создании встречи. */
        forEveryoneWhoComes: string;
        /** «+1 600 каждому, кто на фото» — у плитки фото встречи (баллы — после принятого фото). */
        everyoneOnPhoto: string;
        /** «Организатору · +100 за каждого пришедшего». */
        organizer: string;
        perGuest: string;
        /** «+2 000, если придёт новый человек». */
        ifNewComes: string;
        firstMessageChat: string;
        firstMessageHere: string;
        newFriend: string;
        joinClub: string;
        once: string;
        goodDeed: string;
        /** «+2 000, если друг установит приложение». */
        friendInstalls: string;
        /** «+3 000, когда друг начнёт заниматься». */
        friendStarts: string;
        /** «+18 500, когда друг купит эту часть» — лист «Подарить другу» части. */
        friendBuysThisPart: string;
        /** «+25 000, когда друг купит этот курс» — лист «Подарить другу» курса без частей. */
        friendBuysThisCourse: string;
        /** «+18 500, когда друг купит часть курса» — общая строка приглашения. */
        friendBuysPart: string;
        /** «+18 500, когда друг заберёт подарок» — подарок: приглашённые, как за покупку друга. */
        friendClaimsGift: string;
        /** Выгода первой строкой: «Вам — 10 % с его покупок на баланс: их можно потратить на новые продукты». */
        balance: (pct: number) => string;
        /** Коротко: «вам 10 % с его покупок на баланс». */
        balanceShort: (pct: number) => string;
        /** «Другу −10 %». */
        friendDiscount: (pct: number) => string;
        lessonQuestion: string;
        /** «Вопрос по уроку · +50, когда ответят». */
        whenAnswered: string;
        /** «+3 за каждую минуту клипа». */
        clipMinute: string;
        lecture: string;
        perMinute: string;
        specialistTicket: string;
        productReview: string;
        firstPlan: string;
        /** «+15 000 вклад за пакет токенов». */
        tokenPack: string;
    };
};

const RU: PointsTexts = {
    sphere: {
        contrib: "вклад",
        workouts: "тренировки",
        social: "общественное",
        referrals: "приглашённые",
        learning: "обучение",
    },
    perPurchase: "за покупку",
    forReview: "за отзыв",
    nextPartDiscount: (p) => `−${p} % владельцу предыдущей части`,
    afterAi: "после проверки",
    forQuestion: "за выбранный вопрос",
    oncePerTest: "один раз за этот тест",
    weekOnPlan: "за неделю строго по плану",
    giftTitle: "Подарить курс другу",
    giftBody: (d, b) =>
        `Ссылка есть у каждого курса ниже: другу −${d} %, а вам — ${b} % с его покупки на баланс.`,
    contribForGift: "вклад за подарок",
    hint: {
        meeting: {
            seva: "Встреча-служение",
            health: "Встреча здоровья",
            spirit: "Духовная встреча",
            edu: "Встреча обучения",
            club: "Встреча клуба",
            online: "Онлайн-встреча",
        },
        forEveryoneWhoComes: "каждому, кто придёт",
        everyoneOnPhoto: "каждому, кто на фото",
        organizer: "Организатору",
        perGuest: "за каждого пришедшего",
        ifNewComes: ", если придёт новый человек",
        firstMessageChat: "Первое сообщение в чате",
        firstMessageHere: "Первое сообщение здесь",
        newFriend: "Новый друг",
        joinClub: "Вступить в клуб",
        once: "один раз",
        goodDeed: "Доброе дело",
        friendInstalls: ", если друг установит приложение",
        friendStarts: ", когда друг начнёт заниматься",
        friendBuysThisPart: ", когда друг купит эту часть",
        friendBuysThisCourse: ", когда друг купит этот курс",
        friendBuysPart: ", когда друг купит часть курса",
        friendClaimsGift: ", когда друг заберёт подарок",
        balance: (p) => `Вам — ${p} % с его покупок на баланс: их можно потратить на новые продукты`,
        balanceShort: (p) => `вам ${p} % с его покупок на баланс`,
        friendDiscount: (p) => `другу −${p} %`,
        lessonQuestion: "Вопрос по уроку",
        whenAnswered: ", когда ответят",
        clipMinute: "за каждую минуту клипа",
        lecture: "Лекция",
        perMinute: "за минуту",
        specialistTicket: "Обращение к специалисту",
        productReview: "Отзыв о продукте",
        firstPlan: "Первый план",
        tokenPack: "вклад за пакет токенов",
    },
};

const EN: PointsTexts = {
    sphere: {
        contrib: "contribution",
        workouts: "practice",
        social: "community",
        referrals: "invited",
        learning: "learning",
    },
    perPurchase: "per purchase",
    forReview: "for a review",
    nextPartDiscount: (p) => `−${p}% for the owner of the previous part`,
    afterAi: "once checked",
    forQuestion: "for a chosen question",
    oncePerTest: "once for this test",
    weekOnPlan: "for a week exactly on plan",
    giftTitle: "Give a course to a friend",
    giftBody: (d, b) =>
        `Every course below has its link: −${d}% for your friend, and ${b}% of their purchase to your balance.`,
    contribForGift: "contribution for the gift",
    hint: {
        meeting: {
            seva: "Service meeting",
            health: "Health meeting",
            spirit: "Spiritual meeting",
            edu: "Learning meeting",
            club: "Club meeting",
            online: "Online meeting",
        },
        forEveryoneWhoComes: "for everyone who comes",
        everyoneOnPhoto: "for everyone in the photo",
        organizer: "Organizer",
        perGuest: "for everyone who comes",
        ifNewComes: "if someone new comes",
        firstMessageChat: "First message in a chat",
        firstMessageHere: "Your first message here",
        newFriend: "New friend",
        joinClub: "Join a club",
        once: "once",
        goodDeed: "Good deed",
        friendInstalls: "when a friend installs the app",
        friendStarts: "when a friend starts practising",
        friendBuysThisPart: "when a friend buys this part",
        friendBuysThisCourse: "when a friend buys this course",
        friendBuysPart: "when a friend buys a course part",
        friendClaimsGift: "when a friend claims the gift",
        balance: (p) => `You get ${p}% of their purchases on your balance — spend it on new products`,
        balanceShort: (p) => `${p}% of their purchases to your balance`,
        friendDiscount: (p) => `−${p}% for them`,
        lessonQuestion: "Lesson question",
        whenAnswered: "once answered",
        clipMinute: "for every minute of a clip",
        lecture: "Lecture",
        perMinute: "a minute",
        specialistTicket: "Request to a specialist",
        productReview: "Product review",
        firstPlan: "First plan",
        tokenPack: "contribution for a token pack",
    },
};

const DE: PointsTexts = {
    sphere: {
        contrib: "Beitrag",
        workouts: "Übungen",
        social: "Gemeinschaft",
        referrals: "Eingeladene",
        learning: "Lernen",
    },
    perPurchase: "pro Kauf",
    forReview: "für eine Bewertung",
    nextPartDiscount: (p) => `−${p} % für Besitzer des vorigen Teils`,
    afterAi: "nach der Prüfung",
    forQuestion: "für eine gewählte Frage",
    oncePerTest: "einmal für diesen Test",
    weekOnPlan: "für eine Woche genau nach Plan",
    giftTitle: "Einen Kurs verschenken",
    giftBody: (d, b) =>
        `Jeder Kurs unten hat seinen Link: Ihr Freund bekommt −${d} %, Sie ${b} % seines Kaufs als Guthaben.`,
    contribForGift: "Beitrag für das Geschenk",
    hint: {
        meeting: {
            seva: "Dienst-Treffen",
            health: "Gesundheitstreffen",
            spirit: "Spirituelles Treffen",
            edu: "Lerntreffen",
            club: "Clubtreffen",
            online: "Online-Treffen",
        },
        forEveryoneWhoComes: "für alle, die kommen",
        everyoneOnPhoto: "für alle auf dem Foto",
        organizer: "Für den Organisator",
        perGuest: "für jeden, der kommt",
        ifNewComes: ", wenn jemand Neues kommt",
        firstMessageChat: "Erste Nachricht im Chat",
        firstMessageHere: "Ihre erste Nachricht hier",
        newFriend: "Neuer Freund",
        joinClub: "Einem Club beitreten",
        once: "einmalig",
        goodDeed: "Gute Tat",
        friendInstalls: ", wenn Ihr Freund die App installiert",
        friendStarts: ", wenn Ihr Freund zu üben beginnt",
        friendBuysThisPart: ", wenn Ihr Freund diesen Teil kauft",
        friendBuysThisCourse: ", wenn Ihr Freund diesen Kurs kauft",
        friendBuysPart: ", wenn ein Freund einen Kursteil kauft",
        friendClaimsGift: ", wenn Ihr Freund das Geschenk einlöst",
        balance: (p) => `Sie erhalten ${p} % seiner Käufe als Guthaben – einlösbar für neue Produkte`,
        balanceShort: (p) => `${p} % seiner Käufe als Guthaben für Sie`,
        friendDiscount: (p) => `−${p} % für den Freund`,
        lessonQuestion: "Frage zur Lektion",
        whenAnswered: ", sobald beantwortet",
        clipMinute: "für jede Clip-Minute",
        lecture: "Vortrag",
        perMinute: "pro Minute",
        specialistTicket: "Anfrage an einen Spezialisten",
        productReview: "Produktbewertung",
        firstPlan: "Erster Plan",
        tokenPack: "Beitrag für ein Token-Paket",
    },
};

const ES: PointsTexts = {
    sphere: {
        contrib: "aporte",
        workouts: "prácticas",
        social: "comunidad",
        referrals: "invitados",
        learning: "aprendizaje",
    },
    perPurchase: "por compra",
    forReview: "por una reseña",
    nextPartDiscount: (p) => `−${p} % para quien tiene la parte anterior`,
    afterAi: "tras la revisión",
    forQuestion: "por una pregunta elegida",
    oncePerTest: "una vez por este test",
    weekOnPlan: "por una semana exacta según el plan",
    giftTitle: "Regalar un curso a un amigo",
    giftBody: (d, b) =>
        `Cada curso de abajo tiene su enlace: tu amigo obtiene −${d} % y tú, el ${b} % de su compra en tu saldo.`,
    contribForGift: "aporte por el regalo",
    hint: {
        meeting: {
            seva: "Encuentro de servicio",
            health: "Encuentro de salud",
            spirit: "Encuentro espiritual",
            edu: "Encuentro de aprendizaje",
            club: "Encuentro del club",
            online: "Encuentro en línea",
        },
        forEveryoneWhoComes: "para cada asistente",
        everyoneOnPhoto: "para cada persona en la foto",
        organizer: "Al organizador",
        perGuest: "por cada asistente",
        ifNewComes: ", si viene alguien nuevo",
        firstMessageChat: "Primer mensaje en un chat",
        firstMessageHere: "Tu primer mensaje aquí",
        newFriend: "Nuevo amigo",
        joinClub: "Unirte a un club",
        once: "una vez",
        goodDeed: "Buena acción",
        friendInstalls: ", si tu amigo instala la app",
        friendStarts: ", cuando tu amigo empiece a practicar",
        friendBuysThisPart: ", cuando tu amigo compre esta parte",
        friendBuysThisCourse: ", cuando tu amigo compre este curso",
        friendBuysPart: ", cuando un amigo compre una parte del curso",
        friendClaimsGift: ", cuando tu amigo reciba el regalo",
        balance: (p) => `Recibes el ${p} % de sus compras en tu saldo: úsalo en nuevos productos`,
        balanceShort: (p) => `para ti, el ${p} % de sus compras en tu saldo`,
        friendDiscount: (p) => `−${p} % para tu amigo`,
        lessonQuestion: "Pregunta sobre la lección",
        whenAnswered: ", al recibir respuesta",
        clipMinute: "por cada minuto de clip",
        lecture: "Conferencia",
        perMinute: "por minuto",
        specialistTicket: "Consulta a un especialista",
        productReview: "Reseña de un producto",
        firstPlan: "Primer plan",
        tokenPack: "aporte por un paquete de tokens",
    },
};

const CN: PointsTexts = {
    sphere: {
        contrib: "贡献",
        workouts: "练习",
        social: "社区",
        referrals: "邀请",
        learning: "学习",
    },
    perPurchase: "每次购买",
    forReview: "每条评价",
    nextPartDiscount: (p) => `上一部分的拥有者 −${p}%`,
    afterAi: "审核后",
    forQuestion: "每个选中的问题",
    oncePerTest: "本测验仅一次",
    weekOnPlan: "严格按计划完成一周",
    giftTitle: "把课程送给朋友",
    giftBody: (d, b) => `下面每门课程都有自己的链接：朋友享 −${d}%，您获得其购买额的 ${b}% 存入余额。`,
    contribForGift: "贡献 · 礼物",
    hint: {
        meeting: {
            seva: "服务活动",
            health: "健康聚会",
            spirit: "灵性聚会",
            edu: "学习聚会",
            club: "俱乐部聚会",
            online: "线上聚会",
        },
        forEveryoneWhoComes: "每位到场者",
        everyoneOnPhoto: "照片中的每个人",
        organizer: "组织者",
        perGuest: "每来一人",
        ifNewComes: "，如有新人到场",
        firstMessageChat: "聊天中的第一条消息",
        firstMessageHere: "您在此的第一条消息",
        newFriend: "新朋友",
        joinClub: "加入俱乐部",
        once: "一次",
        goodDeed: "善行",
        friendInstalls: "，朋友安装应用后",
        friendStarts: "，朋友开始练习后",
        friendBuysThisPart: "，朋友购买这一部分后",
        friendBuysThisCourse: "，朋友购买这门课程后",
        friendBuysPart: "，朋友购买课程的一部分后",
        friendClaimsGift: "，朋友领取礼物后",
        balance: (p) => `您获得其购买额的 ${p}% 存入余额：可用于购买新产品`,
        balanceShort: (p) => `您获得其购买额的 ${p}% 存入余额`,
        friendDiscount: (p) => `朋友 −${p}%`,
        lessonQuestion: "课程提问",
        whenAnswered: "，获得回答后",
        clipMinute: "每分钟片段",
        lecture: "讲座",
        perMinute: "每分钟",
        specialistTicket: "向专家咨询",
        productReview: "产品评价",
        firstPlan: "第一个计划",
        tokenPack: "贡献 · 代币包",
    },
};

const AR: PointsTexts = {
    sphere: {
        contrib: "المساهمة",
        workouts: "التمارين",
        social: "المجتمع",
        referrals: "المدعوّون",
        learning: "التعلّم",
    },
    perPurchase: "عن كل شراء",
    forReview: "مقابل المراجعة",
    nextPartDiscount: (p) => `−${p}% لمالك الجزء السابق`,
    afterAi: "بعد التحقق",
    forQuestion: "عن السؤال المختار",
    oncePerTest: "مرة واحدة عن هذا الاختبار",
    weekOnPlan: "عن أسبوع وفق الخطة تمامًا",
    giftTitle: "أهدِ صديقًا دورة",
    giftBody: (d, b) => `لكل دورة أدناه رابطها: لصديقك −${d}%، ولك ${b}% من مشترياته في رصيدك.`,
    contribForGift: "للمساهمة مقابل الهدية",
    hint: {
        meeting: {
            seva: "لقاء خدمة (سيفا)",
            health: "لقاء صحة",
            spirit: "لقاء روحي",
            edu: "لقاء تعلّم",
            club: "لقاء النادي",
            online: "لقاء عبر الإنترنت",
        },
        forEveryoneWhoComes: "لكل من يحضر",
        everyoneOnPhoto: "لكل من في الصورة",
        organizer: "للمنظِّم",
        perGuest: "عن كل حاضر",
        ifNewComes: "، إذا جاء شخص جديد",
        firstMessageChat: "الرسالة الأولى في محادثة",
        firstMessageHere: "رسالتك الأولى هنا",
        newFriend: "صديق جديد",
        joinClub: "انضمّ إلى نادٍ",
        once: "مرة واحدة",
        goodDeed: "عمل طيب",
        friendInstalls: "، إذا ثبّت صديقك التطبيق",
        friendStarts: "، حين يبدأ صديقك الممارسة",
        friendBuysThisPart: "، حين يشتري صديقك هذا الجزء",
        friendBuysThisCourse: "، حين يشتري صديقك هذه الدورة",
        friendBuysPart: "، حين يشتري صديق جزءًا من دورة",
        friendClaimsGift: "، حين يستلم صديقك الهدية",
        balance: (p) => `لك ${p}% من مشترياته في رصيدك — يمكنك إنفاقها على منتجات جديدة`,
        balanceShort: (p) => `لك ${p}% من مشترياته في رصيدك`,
        friendDiscount: (p) => `لصديقك −${p}%`,
        lessonQuestion: "سؤال عن الدرس",
        whenAnswered: "، حين تتلقّى الجواب",
        clipMinute: "عن كل دقيقة من مقطع",
        lecture: "محاضرة",
        perMinute: "للدقيقة",
        specialistTicket: "طلب إلى مختص",
        productReview: "مراجعة منتج",
        firstPlan: "الخطة الأولى",
        tokenPack: "للمساهمة مقابل باقة رموز",
    },
};

const BY_LANG: Record<string, PointsTexts> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, zh: CN };

/**
 * Подписи на языке сайта; незнакомый — en. Арабский — только при включённом арабском
 * интерфейсе (рубильник или предпросмотр, shared/config/arabicUi), иначе /ar — en, как весь сайт там.
 */
export const pointsTexts = (lang: string | undefined): PointsTexts => {
    const l = (lang || "").slice(0, 2).toLowerCase();
    if (l === "ar") return isArabicUi(l) ? AR : EN;
    return BY_LANG[l] || EN;
};
